import { TOURNAMENT_FORMATS } from './formatRegistry';

/**
 * Single rule engine for which tournament formats are usable right now.
 * Every format's enabled/disabled state is decided here — callers just call
 * this once and read the result off the map; a new eligibility rule only
 * ever needs to be added in this one place.
 *
 * @param {number|null} participantCount
 * @param {{ sportName?: string|null, playedBy?: string|null }|null} sportType - the selected sport, or whatever subset of it (sportName/playedBy) is known
 * @returns {Object<string, { enabled: boolean, reason: string|null }>} keyed by format id
 */
export const getEligibleFormats = (participantCount, sportType) => {
  const sportName = sportType?.sportName ?? null;
  const sportPlayedBy = sportType?.playedBy?.toLowerCase() ?? null;
  const result = {};

  for (const format of TOURNAMENT_FORMATS) {
    if (participantCount != null && participantCount < format.minParticipants) {
      result[format.id] = {
        enabled: false,
        reason: `Needs ${format.minParticipants}+ ${format.participantLabel} (${format.minParticipants - participantCount} short)`,
      };
      continue;
    }

    // A format that can't run on a team roster (disallowsTeams) is a
    // non-starter for a sport with NO singles/doubles form at all
    // (played_by: 'team' — cricket, soccer, volleyball, basketball).
    if (format.disallowsTeams && sportPlayedBy === 'team') {
      result[format.id] = {
        enabled: false,
        reason: `${format.label} needs a sport playable as singles/doubles — not a fit for a fixed-roster team sport.`,
      };
      continue;
    }

    // Explicit per-format sport exclusions beyond the played_by rule above
    // (e.g. Ladder + Poker — played_by: 'individual', but doesn't fit the
    // courts-of-4 head-to-head model either).
    if (sportName && format.disallowedSports?.some((s) => s.toLowerCase() === sportName.toLowerCase())) {
      result[format.id] = {
        enabled: false,
        reason: `${format.label} isn't available for ${sportName}.`,
      };
      continue;
    }

    result[format.id] = { enabled: true, reason: null };
  }

  return result;
};
